import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MarketContext = createContext();

export const MARKETS = [
  { 
    code: 'CA', 
    name: { en: 'Canada', fr: 'Canada' }, 
    currency: 'CAD',
    currencySymbol: '$',
    currencyLabel: 'CAD',
    financingPrograms: ['SCHL/CMHC']
  },
  { 
    code: 'US', 
    name: { en: 'United States', fr: 'États-Unis' }, 
    currency: 'USD',
    currencySymbol: '$',
    currencyLabel: 'USD',
    financingPrograms: ['Fannie Mae', 'Freddie Mac', 'FHA 223(f)']
  }
];

// Price mapping: { productSlug: { CA: priceCAD, US: priceUSD } }
export const PRODUCT_PRICES = {
  '6-plex-icf': { CA: 15000, US: 11500 },
  '8-plex-icf': { CA: 22000, US: 16900 },
  '12-plex-icf': { CA: 35000, US: 26900 },
  'plexterra-24': { CA: 55000, US: 42500 },
  'townhomes-icf': { CA: 18000, US: 13900 }
};

// Service prices by market
export const SERVICE_PRICES = {
  'architect-adaptation': { CA: 8500, US: 6500 },
  'structural-engineering': { CA: 12000, US: 9200 },
  'complete-package': { CA: 18500, US: 14200 }
};

export const MarketProvider = ({ children }) => {
  const [market, setMarketRaw] = useState(() => {
    const saved = localStorage.getItem('plexterra-market') || localStorage.getItem('plexterra-market');
    if (saved && ['CA', 'US'].includes(saved)) {
      return saved;
    }
    // Default to Canada until geo-detection resolves
    return 'CA';
  });

  // Wrap setMarket to also flag manual override
  const setMarket = (newMarket) => {
    setMarketRaw(newMarket);
    localStorage.setItem('plexterra-market-manual', '1');
  };

  useEffect(() => {
    localStorage.setItem('plexterra-market', market);
  }, [market]);

  // Auto-detect region on first visit (skip if user already set market manually)
  useEffect(() => {
    const manual = localStorage.getItem('plexterra-market-manual');
    const saved = localStorage.getItem('plexterra-market') || localStorage.getItem('plexterra-market');
    if (manual === '1' || (saved && ['CA', 'US'].includes(saved))) {
      return; // respect user choice / already stored
    }
    axios.get(`${BACKEND_URL}/api/geo/detect`)
      .then((r) => {
        const data = r.data || {};
        if (data.market && ['CA', 'US'].includes(data.market)) {
          setMarketRaw(data.market);
        }
        // Also expose detected language for LanguageContext (via localStorage flag)
        if (data.language && ['en', 'fr', 'es', 'ar', 'zh'].includes(data.language)) {
          const existingLang = localStorage.getItem('hp-language');
          const langManual = localStorage.getItem('hp-language-manual');
          if (!existingLang || langManual !== '1') {
            localStorage.setItem('hp-language', data.language);
            // Notify LanguageContext via a storage event / reload strategy is heavy;
            // instead we dispatch a custom event that LanguageContext can listen to.
            window.dispatchEvent(new CustomEvent('plexterra:geo-language', { detail: data.language }));
          }
        }
      })
      .catch(() => { /* silent — keep default */ });
  }, []);

  const currentMarket = MARKETS.find(m => m.code === market);

  const formatPrice = (priceCAD, priceUSD) => {
    const price = market === 'CA' ? priceCAD : (priceUSD || priceCAD);
    return `${currentMarket.currencySymbol}${price.toLocaleString()} ${currentMarket.currencyLabel}`;
  };

  const getProductPrice = (slug) => {
    const prices = PRODUCT_PRICES[slug];
    if (!prices) return null;
    return prices[market] || prices.CA;
  };

  const getServicePrice = (slug) => {
    const prices = SERVICE_PRICES[slug];
    if (!prices) return null;
    return prices[market] || prices.CA;
  };

  const formatProductPrice = (slug) => {
    const price = getProductPrice(slug);
    if (!price) return '';
    return `${currentMarket.currencySymbol}${price.toLocaleString()} ${currentMarket.currencyLabel}`;
  };

  const formatServicePrice = (slug) => {
    const price = getServicePrice(slug);
    if (!price) return '';
    return `${currentMarket.currencySymbol}${price.toLocaleString()} ${currentMarket.currencyLabel}`;
  };

  return (
    <MarketContext.Provider value={{ 
      market, 
      setMarket, 
      currentMarket,
      markets: MARKETS,
      formatPrice,
      getProductPrice,
      getServicePrice,
      formatProductPrice,
      formatServicePrice,
      currency: currentMarket.currency,
      currencySymbol: currentMarket.currencySymbol,
      currencyLabel: currentMarket.currencyLabel
    }}>
      {children}
    </MarketContext.Provider>
  );
};

export const useMarket = () => {
  const context = useContext(MarketContext);
  if (!context) {
    throw new Error('useMarket must be used within a MarketProvider');
  }
  return context;
};
